import { create } from 'zustand';

/**
 * Globe store — ephemeral camera + interaction state.
 *
 * NOT URL-synced. Camera target is derived from selection / search; the
 * store lets `useGlobeCamera`, the marker layer, and the scene overlays
 * coordinate without prop drilling.
 */
export interface GlobeCameraTarget {
  lat: number;
  lon: number;
  altitude: number;
}

/**
 * Altitude-derived presentation band (PRODUCT.md: "zoom tells the story").
 *
 *   orbit    — whole-Earth view. Clusters only, no labels, auto-rotate allowed.
 *   approach — continental/city view. Clusters split, city labels fade in.
 *   street   — satellite city view. Individual teams presented with labels,
 *              center reticle + nearest-city readout active.
 */
export type ZoomBand = 'orbit' | 'approach' | 'street';

export const BAND_THRESHOLDS = {
  /** altitude ≥ this → orbit */
  orbit: 1.0,
  /** altitude ≥ this (and < orbit) → approach; below → street */
  approach: 0.12,
} as const;

export function bandForAltitude(altitude: number): ZoomBand {
  if (altitude >= BAND_THRESHOLDS.orbit) return 'orbit';
  if (altitude >= BAND_THRESHOLDS.approach) return 'approach';
  return 'street';
}

/** Nearest catalogued city to the camera center (street/approach readout). */
export interface FocusCity {
  citySlug: string;
  city: string;
  country: string | null;
  teamCount: number;
}

export interface GlobeStore {
  cameraTarget: GlobeCameraTarget | null;
  /** Live camera altitude, updated (throttled) from onZoom. */
  altitude: number;
  band: ZoomBand;
  /** True once the user has manually dragged/zoomed — kills auto-rotate. */
  userTouched: boolean;
  /** Timestamp (performance.now) of the last manual drag/zoom gesture. */
  lastInteractionAt: number;
  focusCity: FocusCity | null;
  hoveredCluster: string | null; // city slug
  hoveredTeam: string | null; // team number
  reduceMotion: boolean;
  setCameraTarget: (target: GlobeCameraTarget | null) => void;
  setAltitude: (altitude: number) => void;
  setUserTouched: (value: boolean) => void;
  markInteraction: () => void;
  setFocusCity: (focus: FocusCity | null) => void;
  setHoveredCluster: (slug: string | null) => void;
  setHoveredTeam: (teamNumber: string | null) => void;
  setReduceMotion: (value: boolean) => void;
}

export const useGlobeStore = create<GlobeStore>((set, get) => ({
  cameraTarget: null,
  altitude: 2.5,
  band: 'orbit',
  userTouched: false,
  lastInteractionAt: 0,
  focusCity: null,
  hoveredCluster: null,
  hoveredTeam: null,
  reduceMotion: false,
  setCameraTarget: (target) => set({ cameraTarget: target }),
  setAltitude: (altitude) => {
    const band = bandForAltitude(altitude);
    const prev = get();
    if (prev.altitude === altitude && prev.band === band) return;
    set({ altitude, band });
  },
  setUserTouched: (value) => {
    if (get().userTouched !== value) set({ userTouched: value });
  },
  markInteraction: () => set({ lastInteractionAt: performance.now() }),
  setFocusCity: (focus) => {
    const prev = get().focusCity;
    if (prev?.citySlug === focus?.citySlug) return;
    set({ focusCity: focus });
  },
  setHoveredCluster: (slug) => set({ hoveredCluster: slug }),
  setHoveredTeam: (teamNumber) => set({ hoveredTeam: teamNumber }),
  setReduceMotion: (value) => set({ reduceMotion: value }),
}));
